import {
  CommonControlsHierarchyResponse,
  ControlMapping,
  DynamoDBQueryResponse,
  Framework,
  FrameworkControl,
  MappingFeedback,
  MappingQueryParams,
  MappingReview,
  MappingWithReviews,
  ReviewQueryParams,
} from '../../types/dynamodb';
import {
  ListControlsResponse,
  ListFrameworksResponse,
  ListMappingsResponse,
  MappingsForControlResponse,
  nexusApiService as legacyNexusApiService,
} from '../nexusApiService';
import { NexusControlService } from './NexusControlService';
import { NexusFrameworkService } from './NexusFrameworkService';
import { NexusMappingFeedbackService } from './NexusMappingFeedbackService';
import { NexusMappingReviewService } from './NexusMappingReviewService';
import { NexusMappingService } from './NexusMappingService';

/**
 * Composed API facade using backend-named sub-services.
 * Keeps existing UI-facing method signatures stable.
 */
export class NexusApiOrchestrationService {
  private static readonly COMMON_CONTROLS_FRAMEWORK_NAME = 'Common-Controls';
  private static readonly COMMON_CONTROLS_FALLBACK_FRAMEWORK_NAME = 'CommonControls';
  private static readonly COMMON_CONTROLS_FRAMEWORK_VERSION = '1.0';
  private static readonly COMMON_CONTROLS_FRAMEWORK_KEY = 'Common-Controls#1.0';
  private static readonly DEFAULT_PAGE_SIZE = 50;
  private static readonly CONTROL_FETCH_BATCH_SIZE = 10;
  private static readonly MAX_THROTTLE_RETRIES = 5;
  private static readonly RETRY_BASE_DELAY_MS = 200;
  private static readonly PREVIEW_MIN_CONTROL_PROBES = 12;
  private static readonly PREVIEW_MAX_CONTROL_PROBES = 40;
  private static readonly PREVIEW_PROBE_MULTIPLIER = 1.5;
  private static readonly PREVIEW_EMPTY_RESULT_PROBE_MULTIPLIER = 3;
  private static readonly COMMON_CONTROLS_CACHE_TTL_MS = 15 * 60 * 1000;
  private static readonly INTER_BATCH_DELAY_MS = 25;
  private static readonly MAPPINGS_QUERY_SESSION_CACHE_PREFIX =
    'nexus.orch.mappingsQuery.v1::';

  readonly nexusFrameworkService: NexusFrameworkService;
  readonly nexusControlService: NexusControlService;
  readonly nexusMappingService: NexusMappingService;
  readonly nexusMappingReviewService: NexusMappingReviewService;
  readonly nexusMappingFeedbackService: NexusMappingFeedbackService;
  private commonControlsCache:
    | {
        controls: FrameworkControl[];
        expiresAt: number;
      }
    | undefined;
  private controlMappingsCache = new Map<
    string,
    { mappings: ControlMapping[]; expiresAt: number }
  >();
  private controlMappingsInFlight = new Map<string, Promise<ControlMapping[]>>();
  private mappingsQueryCache = new Map<
    string,
    { mappings: ControlMapping[]; expiresAt: number }
  >();
  private mappingsQueryInFlight = new Map<string, Promise<ControlMapping[]>>();

  constructor() {
    this.nexusFrameworkService = new NexusFrameworkService(legacyNexusApiService);
    this.nexusControlService = new NexusControlService(legacyNexusApiService);
    this.nexusMappingService = new NexusMappingService(legacyNexusApiService);
    this.nexusMappingReviewService = new NexusMappingReviewService(legacyNexusApiService);
    this.nexusMappingFeedbackService = new NexusMappingFeedbackService(legacyNexusApiService);
  }

  // ---------------------------------------------------------------------------
  // DataService contract methods (consumed by dataServiceFactory)
  // ---------------------------------------------------------------------------

  async getAllMappingsWithReviews(
    params?: MappingQueryParams
  ): Promise<MappingWithReviews[]> {
    const allMappings = await this.listMappingsFromBackendWithCache(params);
    const limitedMappings = params?.limit ? allMappings.slice(0, params.limit) : allMappings;
    const includeReviews = params?.includeReviews !== false;

    if (!includeReviews) {
      return limitedMappings.map((mapping) => ({
        mapping,
        reviews: [],
        feedback: undefined,
      }));
    }

    const mappingsWithReviews: MappingWithReviews[] = [];
    const batchSize = 10;
    for (let i = 0; i < limitedMappings.length; i += batchSize) {
      const batch = limitedMappings.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(async (mapping) => {
          try {
            const reviews = await this.nexusMappingReviewService.getReviewsForMapping(
              mapping.mappingKey,
              mapping
            );
            return {
              mapping,
              reviews,
              feedback: undefined,
            };
          } catch (error) {
            console.warn(`Failed to fetch reviews for ${mapping.mappingKey}:`, error);
            return {
              mapping,
              reviews: [],
              feedback: undefined,
            };
          }
        })
      );
      mappingsWithReviews.push(...batchResults);
    }

    return mappingsWithReviews;
  }

  async getMappingWithReviews(mappingKey: string): Promise<MappingWithReviews | null> {
    return legacyNexusApiService.getMappingWithReviews(mappingKey);
  }

  async getAllMappingsForLabeling(currentUserId: string): Promise<MappingWithReviews[]> {
    const mappings = await this.listMappingsFromBackend({
      status: 'LABELING_IN_PROGRESS' as any,
    });
    const mappingIds = mappings.map((m) => m.mappingKey);
    const userFeedbackMap = await this.nexusMappingFeedbackService.getUserFeedbackForMappings(
      mappingIds,
      currentUserId
    );

    return mappings.map((mapping) => ({
      mapping,
      reviews: [],
      feedback: userFeedbackMap.get(mapping.mappingKey) as any,
    }));
  }

  async getCommonControlsWithHierarchy(): Promise<CommonControlsHierarchyResponse> {
    return this.nexusControlService.getCommonControlsWithHierarchy();
  }

  async getAvailableFrameworks(): Promise<Framework[]> {
    const frameworksByKey = new Map<string, Framework>();
    let nextToken: string | undefined;

    do {
      const response = await this.nexusFrameworkService.listFrameworks({
        status: 'ACTIVE',
        maxResults: NexusApiOrchestrationService.DEFAULT_PAGE_SIZE,
        nextToken,
      });

      response.frameworks.forEach((framework) => {
        if (!framework.frameworkName || !framework.version) {
          return;
        }
        frameworksByKey.set(
          `${framework.frameworkName}#${framework.version}`,
          framework
        );
      });

      const previousToken = nextToken;
      nextToken = response.nextToken;

      // Guard against non-progressing tokens from backend pagination.
      if (nextToken && nextToken === previousToken) {
        break;
      }
    } while (nextToken);

    return Array.from(frameworksByKey.values());
  }

  async getReviewsForMapping(mappingKey: string): Promise<MappingReview[]> {
    return this.nexusMappingReviewService.getReviewsForMapping(mappingKey);
  }

  async getReviewsByReviewer(
    params: ReviewQueryParams
  ): Promise<DynamoDBQueryResponse<MappingReview>> {
    return this.nexusMappingReviewService.getReviewsByReviewer(params);
  }

  async submitReview(review: Omit<MappingReview, 'reviewKey' | 'submittedAt'>): Promise<void> {
    return this.nexusMappingReviewService.submitReview(review);
  }

  async suggestMapping(params: {
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    justification: string;
  }): Promise<void> {
    return this.nexusMappingReviewService.suggestMapping(params);
  }

  async submitLabel(params: {
    mappingKey: string;
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    decision: 'label-yes' | 'label-no';
    feedback?: string;
  }): Promise<void> {
    return this.nexusMappingReviewService.submitLabel(params);
  }

  async submitFinalFeedback(
    feedback: Omit<MappingFeedback, 'timestamp'>
  ): Promise<void> {
    return this.nexusMappingFeedbackService.submitFinalFeedback(feedback);
  }

  async getUserFeedbackForMappings(
    mappingIds: string[],
    userId: string
  ): Promise<Map<string, { label: string; feedback?: string; feedbackId: string }>> {
    return this.nexusMappingFeedbackService.getUserFeedbackForMappings(mappingIds, userId);
  }

  async submitFeedback(
    mappingId: string,
    data: {
      feedbackProviderId: string;
      label: 'thumbs_up' | 'thumbs_down';
      decision: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string; label: string }> {
    return this.nexusMappingFeedbackService.submitFeedback(mappingId, data);
  }

  async updateFeedback(
    mappingId: string,
    feedbackId: string,
    data: {
      label?: 'thumbs_up' | 'thumbs_down';
      decision?: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string }> {
    return this.nexusMappingFeedbackService.updateFeedback(mappingId, feedbackId, data);
  }

  async batchGetFrameworkControls(
    controls: Array<{ frameworkKey: string; controlKey: string }>
  ): Promise<Map<string, FrameworkControl>> {
    return this.nexusControlService.batchGetFrameworkControls(controls);
  }

  // ---------------------------------------------------------------------------
  // Additional pass-throughs used by advanced flows
  // ---------------------------------------------------------------------------

  async listMappings(params?: MappingQueryParams): Promise<ListMappingsResponse> {
    return this.nexusMappingService.listMappings(params);
  }

  async getMapping(mappingId: string): Promise<ControlMapping> {
    return this.nexusMappingService.getMapping(mappingId);
  }

  async getMappingsForControl(
    controlId: string,
    params?: {
      status?: string;
      frameworkName?: string;
      frameworkVersion?: string;
      maxResults?: number;
      nextToken?: string;
    }
  ): Promise<MappingsForControlResponse> {
    return this.nexusMappingService.getMappingsForControl(controlId, params);
  }

  async listFrameworks(params?: {
    status?: string;
    maxResults?: number;
    nextToken?: string;
  }): Promise<ListFrameworksResponse> {
    return this.nexusFrameworkService.listFrameworks(params);
  }

  async listFrameworkVersions(
    frameworkName: string,
    params?: { maxResults?: number; nextToken?: string }
  ): Promise<ListFrameworksResponse> {
    return this.nexusFrameworkService.listFrameworkVersions(frameworkName, params);
  }

  async getFramework(
    frameworkName: string,
    frameworkVersion: string
  ): Promise<Framework> {
    return this.nexusFrameworkService.getFramework(frameworkName, frameworkVersion);
  }

  async listControls(
    frameworkName: string,
    frameworkVersion: string,
    params?: { maxResults?: number; nextToken?: string; status?: string }
  ): Promise<ListControlsResponse> {
    return this.nexusControlService.listControls(frameworkName, frameworkVersion, params);
  }

  async getFrameworkControlsByKey(frameworkKey: string): Promise<FrameworkControl[]> {
    return this.nexusControlService.getFrameworkControlsByKey(frameworkKey);
  }

  async getControl(
    frameworkName: string,
    frameworkVersion: string,
    controlId: string
  ): Promise<FrameworkControl> {
    return this.nexusControlService.getControl(frameworkName, frameworkVersion, controlId);
  }

  private async listMappingsFromBackend(params?: MappingQueryParams): Promise<ControlMapping[]> {
    const hasControlScope = Boolean(params?.controlKey);
    const hasFrameworkScope = Boolean(params?.frameworkKey);
    const isCommonControlsFramework = this.isCommonControlsFrameworkKey(
      params?.frameworkKey
    );

    if (hasControlScope) {
      return this.listMappingsWithinScope(params);
    }

    if (!hasFrameworkScope) {
      // GetMappings is effectively control-scoped in backend behavior.
      // For no-scope preview, prioritize active framework fan-out (faster when
      // Common-Controls has no mapping records), then fall back to
      // Common-Controls fan-out for ontology-backed environments.
      const frameworkMappings = await this.listMappingsAcrossActiveFrameworks(
        params?.status,
        params?.limit
      );
      if (frameworkMappings.length > 0 || params?.limit === 0) {
        return frameworkMappings;
      }
      return this.listMappingsAcrossCommonControls(params?.status, params?.limit);
    }

    if (isCommonControlsFramework) {
      return this.listMappingsAcrossCommonControls(params?.status, params?.limit);
    }

    // Framework-scoped query:
    // Try selected framework controls first.
    // If backend indexing returns empty, fall back to Common-Controls fan-out
    // and apply selected framework filtering locally.
    const frameworkScopedMappings = await this.listMappingsAcrossFrameworkControls(
      params.frameworkKey,
      params?.status,
      params?.limit
    );

    if (frameworkScopedMappings.length > 0 || params?.limit === 0) {
      return frameworkScopedMappings;
    }

    return this.listMappingsAcrossCommonControls(
      params?.status,
      params?.limit,
      params.frameworkKey
    );
  }

  private async listMappingsFromBackendWithCache(
    params?: MappingQueryParams
  ): Promise<ControlMapping[]> {
    const cacheKey = this.getMappingsQueryCacheKey(params);
    const cachedMappings = this.getCachedMappingsQuery(cacheKey);
    if (cachedMappings) {
      return cachedMappings;
    }

    const inFlight = this.mappingsQueryInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const fetchPromise = (async () => {
      const mappings = await this.listMappingsFromBackend(params);
      this.setCachedMappingsQuery(cacheKey, mappings);
      return mappings;
    })();

    this.mappingsQueryInFlight.set(cacheKey, fetchPromise);
    try {
      return await fetchPromise;
    } finally {
      this.mappingsQueryInFlight.delete(cacheKey);
    }
  }

  private async listMappingsWithinScope(params?: MappingQueryParams): Promise<ControlMapping[]> {
    const mappings: ControlMapping[] = [];
    let nextToken = params?.nextToken;
    let remaining = params?.limit;

    do {
      const pageLimit = remaining
        ? Math.min(remaining, NexusApiOrchestrationService.DEFAULT_PAGE_SIZE)
        : NexusApiOrchestrationService.DEFAULT_PAGE_SIZE;
      const response = await this.nexusMappingService.listMappings({
        ...params,
        limit: pageLimit,
        nextToken,
      });
      mappings.push(...response.mappings);
      nextToken = response.nextToken;

      if (remaining) {
        remaining -= response.mappings.length;
      }
    } while (nextToken && (!remaining || remaining > 0));

    return mappings;
  }

  private async listAllCommonControls(): Promise<FrameworkControl[]> {
    const now = Date.now();
    if (this.commonControlsCache && this.commonControlsCache.expiresAt > now) {
      return this.commonControlsCache.controls;
    }

    const candidateFrameworkNames = [
      NexusApiOrchestrationService.COMMON_CONTROLS_FRAMEWORK_NAME,
      NexusApiOrchestrationService.COMMON_CONTROLS_FALLBACK_FRAMEWORK_NAME,
    ];
    let controls: FrameworkControl[] = [];
    let lastError: unknown;

    for (const frameworkName of candidateFrameworkNames) {
      const fetchedControls: FrameworkControl[] = [];
      let nextToken: string | undefined;
      try {
        do {
          const response = await this.nexusControlService.listControls(
            frameworkName,
            NexusApiOrchestrationService.COMMON_CONTROLS_FRAMEWORK_VERSION,
            {
              status: 'active',
              maxResults: NexusApiOrchestrationService.DEFAULT_PAGE_SIZE,
              nextToken,
            }
          );
          fetchedControls.push(...response.controls);
          nextToken = response.nextToken;
        } while (nextToken);

        if (fetchedControls.length > 0) {
          controls = fetchedControls;
          break;
        }
      } catch (error) {
        lastError = error;
      }
    }

    if (controls.length === 0 && lastError) {
      throw lastError;
    }

    this.commonControlsCache = {
      controls,
      expiresAt: now + NexusApiOrchestrationService.COMMON_CONTROLS_CACHE_TTL_MS,
    };

    return controls;
  }

  private async listMappingsForControlKey(
    controlKey: string,
    status?: MappingQueryParams['status'],
    limit?: number
  ): Promise<ControlMapping[]> {
    const parts = controlKey.split('#');
    if (parts.length < 3) {
      return [];
    }

    const cacheKey = this.getControlMappingsCacheKey(controlKey, status);
    const cachedMappings = this.getCachedControlMappings(cacheKey);
    if (cachedMappings) {
      return limit !== undefined ? cachedMappings.slice(0, limit) : cachedMappings;
    }

    const existingInFlight = this.controlMappingsInFlight.get(cacheKey);
    if (existingInFlight) {
      const inFlightMappings = await existingInFlight;
      return limit !== undefined ? inFlightMappings.slice(0, limit) : inFlightMappings;
    }

    const frameworkName = parts[0];
    const frameworkVersion = parts[1];
    const controlId = parts.slice(2).join('#');

    const fetchPromise = (async () => {
      const frameworkNamesToTry = this.getFrameworkNameCandidates(frameworkName);
      const mappings: ControlMapping[] = [];
      for (const candidateFrameworkName of frameworkNamesToTry) {
        // Cache full control mappings and apply request-specific limit via slicing.
        const candidateMappings = await this.listMappingsForControlInFramework(
          controlId,
          candidateFrameworkName,
          frameworkVersion,
          status
        );
        mappings.push(...candidateMappings);

        if (mappings.length > 0) {
          break;
        }
      }

      const dedupedMappings = this.dedupeMappings(mappings);
      this.controlMappingsCache.set(cacheKey, {
        mappings: dedupedMappings,
        expiresAt: Date.now() + NexusApiOrchestrationService.COMMON_CONTROLS_CACHE_TTL_MS,
      });
      return dedupedMappings;
    })();

    this.controlMappingsInFlight.set(cacheKey, fetchPromise);
    try {
      const dedupedMappings = await fetchPromise;
      return limit !== undefined ? dedupedMappings.slice(0, limit) : dedupedMappings;
    } finally {
      this.controlMappingsInFlight.delete(cacheKey);
    }
  }

  private async listMappingsForControlKeyWithRetry(
    controlKey: string,
    status?: MappingQueryParams['status'],
    limit?: number
  ): Promise<ControlMapping[]> {
    let attempt = 0;

    while (true) {
      try {
        return await this.listMappingsForControlKey(controlKey, status, limit);
      } catch (error) {
        attempt += 1;
        if (
          !this.isThrottlingError(error) ||
          attempt >= NexusApiOrchestrationService.MAX_THROTTLE_RETRIES
        ) {
          throw error;
        }

        const delayMs =
          NexusApiOrchestrationService.RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) +
          Math.floor(Math.random() * 100);
        await this.delay(delayMs);
      }
    }
  }

  private async listMappingsAcrossCommonControls(
    status?: MappingQueryParams['status'],
    limit?: number,
    frameworkKey?: string
  ): Promise<ControlMapping[]> {
    const controls = await this.listAllCommonControls();
    const controlKeys = this.shuffle(
      Array.from(
        new Set(
          controls
            .map((control) => control.controlKey)
            .filter((key): key is string => Boolean(key))
        )
      )
    );
    const controlProbeLimit = this.resolveControlProbeLimit(controlKeys.length, limit);
    const expandedProbeLimit = this.resolveExpandedControlProbeLimit(
      controlKeys.length,
      controlProbeLimit
    );

    if (limit !== undefined && limit <= 0) {
      return [];
    }

    const uniqueMappings = new Map<string, ControlMapping>();
    let failedRequests = 0;
    let processedControlCount = 0;
    for (
      let i = 0;
      i < controlKeys.length;
      i += NexusApiOrchestrationService.CONTROL_FETCH_BATCH_SIZE
    ) {
      const batch = controlKeys.slice(
        i,
        i + NexusApiOrchestrationService.CONTROL_FETCH_BATCH_SIZE
      );
      processedControlCount += batch.length;
      const batchResults = await Promise.all(
        batch.map(async (controlKey) => {
          try {
            const remaining =
              limit !== undefined ? Math.max(limit - uniqueMappings.size, 0) : undefined;

            if (remaining !== undefined && remaining <= 0) {
              return [];
            }

            return await this.listMappingsForControlKeyWithRetry(
              controlKey,
              status,
              remaining
            );
          } catch (error) {
            failedRequests += 1;
            console.warn(
              `Failed to fetch mappings for controlKey ${controlKey}:`,
              error
            );
            return [];
          }
        })
      );
      batchResults.forEach((mappings) => {
        mappings.forEach((mapping) => {
          if (frameworkKey && !this.mappingMatchesFramework(mapping, frameworkKey)) {
            return;
          }

          const key =
            mapping.mappingKey || `${mapping.controlKey}#${mapping.mappedControlKey}`;
          if (!key) {
            return;
          }
          uniqueMappings.set(key, mapping);
        });
      });

      if (limit !== undefined && uniqueMappings.size >= limit) {
        break;
      }

      // Keep preview requests bounded, but allow a wider second window when
      // first probes return empty to avoid false-empty previews.
      if (
        limit !== undefined &&
        processedControlCount >= controlProbeLimit &&
        uniqueMappings.size > 0
      ) {
        break;
      }

      if (limit !== undefined && processedControlCount >= expandedProbeLimit) {
        break;
      }

      if (
        i + NexusApiOrchestrationService.CONTROL_FETCH_BATCH_SIZE <
        controlKeys.length
      ) {
        await this.delay(NexusApiOrchestrationService.INTER_BATCH_DELAY_MS);
      }
    }

    if (processedControlCount > 0 && failedRequests === processedControlCount) {
      throw new Error(
        'Mapping fetch was throttled for all controls. Retry after traffic cools down.'
      );
    }

    const mappings = Array.from(uniqueMappings.values());
    return limit !== undefined ? mappings.slice(0, limit) : mappings;
  }

  private async listMappingsAcrossActiveFrameworks(
    status?: MappingQueryParams['status'],
    limit?: number
  ): Promise<ControlMapping[]> {
    const frameworks = await this.getAvailableFrameworks();
    const frameworkKeys = this.shuffle(
      frameworks
        .filter((framework) => framework.frameworkName && framework.version)
        .filter((framework) => !this.isCommonControlsFrameworkName(framework.frameworkName!))
        .filter((framework) => !/^AWS[.-]?/i.test(framework.frameworkName || ''))
        .map((framework) => `${framework.frameworkName}#${framework.version}`)
    );

    if (limit !== undefined && limit <= 0) {
      return [];
    }

    const uniqueMappings = new Map<string, ControlMapping>();
    let failedFrameworks = 0;
    for (const frameworkKey of frameworkKeys) {
      try {
        const remaining =
          limit !== undefined ? Math.max(limit - uniqueMappings.size, 0) : undefined;
        if (remaining !== undefined && remaining <= 0) {
          break;
        }

        const mappings = await this.listMappingsAcrossFrameworkControls(
          frameworkKey,
          status,
          remaining
        );
        mappings.forEach((mapping) => {
          const key =
            mapping.mappingKey || `${mapping.controlKey}#${mapping.mappedControlKey}`;
          if (!key) {
            return;
          }
          uniqueMappings.set(key, mapping);
        });

        if (limit !== undefined && uniqueMappings.size >= limit) {
          break;
        }
      } catch (error) {
        failedFrameworks += 1;
        console.warn(`Failed to fetch mappings for framework ${frameworkKey}:`, error);
      }
    }

    if (frameworkKeys.length > 0 && failedFrameworks === frameworkKeys.length) {
      throw new Error(
        'Failed to fetch mappings across active frameworks. The API may be throttling or unavailable.'
      );
    }

    const mappings = Array.from(uniqueMappings.values());
    return limit !== undefined ? mappings.slice(0, limit) : mappings;
  }

  private async listMappingsAcrossFrameworkControls(
    frameworkKey: string,
    status?: MappingQueryParams['status'],
    limit?: number
  ): Promise<ControlMapping[]> {
    const controls = await this.nexusControlService.getFrameworkControlsByKey(frameworkKey);
    const controlKeys = this.shuffle(
      Array.from(
        new Set(
          controls
            .map((control) => control.controlKey)
            .filter((key): key is string => Boolean(key))
        )
      )
    );
    const controlProbeLimit =
      limit !== undefined
        ? this.resolveControlProbeLimit(controlKeys.length, limit)
        : controlKeys.length;
    const expandedProbeLimit =
      limit !== undefined
        ? this.resolveExpandedControlProbeLimit(controlKeys.length, controlProbeLimit)
        : controlKeys.length;

    if (limit !== undefined && limit <= 0) {
      return [];
    }

    const uniqueMappings = new Map<string, ControlMapping>();
    let failedRequests = 0;
    let processedControlCount = 0;
    for (
      let i = 0;
      i < controlKeys.length;
      i += NexusApiOrchestrationService.CONTROL_FETCH_BATCH_SIZE
    ) {
      const batch = controlKeys.slice(
        i,
        i + NexusApiOrchestrationService.CONTROL_FETCH_BATCH_SIZE
      );
      processedControlCount += batch.length;
      const batchResults = await Promise.all(
        batch.map(async (controlKey) => {
          try {
            const remaining =
              limit !== undefined ? Math.max(limit - uniqueMappings.size, 0) : undefined;

            if (remaining !== undefined && remaining <= 0) {
              return [];
            }

            return await this.listMappingsForControlKeyWithRetry(
              controlKey,
              status,
              remaining
            );
          } catch (error) {
            failedRequests += 1;
            console.warn(
              `Failed to fetch mappings for framework controlKey ${controlKey}:`,
              error
            );
            return [];
          }
        })
      );

      batchResults.forEach((mappings) => {
        mappings.forEach((mapping) => {
          const key =
            mapping.mappingKey || `${mapping.controlKey}#${mapping.mappedControlKey}`;
          if (!key) {
            return;
          }
          uniqueMappings.set(key, mapping);
        });
      });

      if (limit !== undefined && uniqueMappings.size >= limit) {
        break;
      }

      if (
        limit !== undefined &&
        processedControlCount >= controlProbeLimit &&
        uniqueMappings.size > 0
      ) {
        break;
      }

      if (limit !== undefined && processedControlCount >= expandedProbeLimit) {
        break;
      }

      if (i + NexusApiOrchestrationService.CONTROL_FETCH_BATCH_SIZE < controlKeys.length) {
        await this.delay(NexusApiOrchestrationService.INTER_BATCH_DELAY_MS);
      }
    }

    if (processedControlCount > 0 && failedRequests === processedControlCount) {
      throw new Error(
        'Mapping fetch was throttled for all framework controls. Retry after traffic cools down.'
      );
    }

    const mappings = Array.from(uniqueMappings.values());
    return limit !== undefined ? mappings.slice(0, limit) : mappings;
  }

  private mappingMatchesFramework(
    mapping: ControlMapping,
    frameworkKey: string
  ): boolean {
    const [frameworkName, frameworkVersion] = frameworkKey.split('#');
    if (!frameworkName || !frameworkVersion) {
      return true;
    }

    return (
      this.controlKeyMatchesFramework(
        mapping.controlKey,
        frameworkName,
        frameworkVersion
      ) ||
      this.controlKeyMatchesFramework(
        mapping.mappedControlKey,
        frameworkName,
        frameworkVersion
      )
    );
  }

  private controlKeyMatchesFramework(
    controlKey: string | undefined,
    frameworkName: string,
    frameworkVersion: string
  ): boolean {
    if (!controlKey) {
      return false;
    }

    const parts = controlKey.split('#');
    return (
      this.normalizeFrameworkName(parts[0] || '') ===
        this.normalizeFrameworkName(frameworkName) &&
      parts[1] === frameworkVersion
    );
  }

  private resolveControlProbeLimit(
    totalControlKeys: number,
    limit?: number
  ): number {
    if (!limit) {
      return totalControlKeys;
    }

    const previewProbeBudget = Math.max(
      NexusApiOrchestrationService.PREVIEW_MIN_CONTROL_PROBES,
      Math.min(
        NexusApiOrchestrationService.PREVIEW_MAX_CONTROL_PROBES,
        limit * NexusApiOrchestrationService.PREVIEW_PROBE_MULTIPLIER
      )
    );
    return Math.min(totalControlKeys, previewProbeBudget);
  }

  private resolveExpandedControlProbeLimit(
    totalControlKeys: number,
    controlProbeLimit: number
  ): number {
    const expandedProbeBudget = Math.ceil(
      controlProbeLimit *
        NexusApiOrchestrationService.PREVIEW_EMPTY_RESULT_PROBE_MULTIPLIER
    );
    return Math.min(totalControlKeys, expandedProbeBudget);
  }

  private normalizeFrameworkName(name: string): string {
    return name.replace(/[-_]/g, '').toLowerCase();
  }

  private getMappingsQueryCacheKey(params?: MappingQueryParams): string {
    const normalized = {
      controlKey: params?.controlKey || '',
      frameworkKey: params?.frameworkKey || '',
      status: params?.status || '',
      nextToken: params?.nextToken || '',
      limit: params?.limit ?? '',
    };
    return JSON.stringify(normalized);
  }

  private getCachedMappingsQuery(cacheKey: string): ControlMapping[] | undefined {
    const now = Date.now();
    const inMemory = this.mappingsQueryCache.get(cacheKey);
    if (inMemory) {
      if (inMemory.expiresAt > now) {
        return inMemory.mappings;
      }
      this.mappingsQueryCache.delete(cacheKey);
    }

    const sessionCacheKey = `${NexusApiOrchestrationService.MAPPINGS_QUERY_SESSION_CACHE_PREFIX}${cacheKey}`;
    const payload = this.safeReadSessionCache(sessionCacheKey) as
      | { mappings?: ControlMapping[]; expiresAt?: number }
      | undefined;
    if (!payload || !Array.isArray(payload.mappings) || typeof payload.expiresAt !== 'number') {
      return undefined;
    }
    if (payload.expiresAt <= now) {
      this.safeRemoveSessionCache(sessionCacheKey);
      return undefined;
    }

    this.mappingsQueryCache.set(cacheKey, {
      mappings: payload.mappings,
      expiresAt: payload.expiresAt,
    });
    return payload.mappings;
  }

  private setCachedMappingsQuery(cacheKey: string, mappings: ControlMapping[]): void {
    const expiresAt = Date.now() + NexusApiOrchestrationService.COMMON_CONTROLS_CACHE_TTL_MS;
    this.mappingsQueryCache.set(cacheKey, { mappings, expiresAt });
    const sessionCacheKey = `${NexusApiOrchestrationService.MAPPINGS_QUERY_SESSION_CACHE_PREFIX}${cacheKey}`;
    this.safeWriteSessionCache(sessionCacheKey, { mappings, expiresAt });
  }

  private safeReadSessionCache(cacheKey: string): unknown {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return undefined;
      }
      const raw = window.sessionStorage.getItem(cacheKey);
      if (!raw) {
        return undefined;
      }
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  }

  private safeWriteSessionCache(cacheKey: string, value: unknown): void {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
      }
      window.sessionStorage.setItem(cacheKey, JSON.stringify(value));
    } catch {
      // best-effort cache only
    }
  }

  private safeRemoveSessionCache(cacheKey: string): void {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
      }
      window.sessionStorage.removeItem(cacheKey);
    } catch {
      // best-effort cache only
    }
  }

  private getControlMappingsCacheKey(
    controlKey: string,
    status?: MappingQueryParams['status']
  ): string {
    return `${controlKey}::${String(status || '').toLowerCase()}`;
  }

  private getCachedControlMappings(cacheKey: string): ControlMapping[] | undefined {
    const cached = this.controlMappingsCache.get(cacheKey);
    if (!cached) {
      return undefined;
    }

    if (cached.expiresAt <= Date.now()) {
      this.controlMappingsCache.delete(cacheKey);
      return undefined;
    }

    return cached.mappings;
  }

  private isCommonControlsFrameworkName(name: string): boolean {
    return this.normalizeFrameworkName(name) === 'commoncontrols';
  }

  private getFrameworkNameCandidates(frameworkName: string): string[] {
    if (!this.isCommonControlsFrameworkName(frameworkName)) {
      return [frameworkName];
    }

    const candidates = [frameworkName];
    if (frameworkName === NexusApiOrchestrationService.COMMON_CONTROLS_FRAMEWORK_NAME) {
      candidates.push(
        NexusApiOrchestrationService.COMMON_CONTROLS_FALLBACK_FRAMEWORK_NAME
      );
    } else if (
      frameworkName ===
      NexusApiOrchestrationService.COMMON_CONTROLS_FALLBACK_FRAMEWORK_NAME
    ) {
      candidates.push(NexusApiOrchestrationService.COMMON_CONTROLS_FRAMEWORK_NAME);
    } else {
      candidates.push(NexusApiOrchestrationService.COMMON_CONTROLS_FRAMEWORK_NAME);
      candidates.push(
        NexusApiOrchestrationService.COMMON_CONTROLS_FALLBACK_FRAMEWORK_NAME
      );
    }
    return candidates;
  }

  private async listMappingsForControlInFramework(
    controlId: string,
    frameworkName: string,
    frameworkVersion: string,
    status?: MappingQueryParams['status'],
    limit?: number
  ): Promise<ControlMapping[]> {
    const mappings: ControlMapping[] = [];
    let nextToken: string | undefined;
    let remaining = limit;

    do {
      const maxResults = remaining
        ? Math.min(remaining, NexusApiOrchestrationService.DEFAULT_PAGE_SIZE)
        : NexusApiOrchestrationService.DEFAULT_PAGE_SIZE;

      const response = await this.nexusMappingService.getMappingsForControl(controlId, {
        frameworkName,
        frameworkVersion,
        status,
        maxResults,
        nextToken,
      });
      mappings.push(...response.mappings);
      nextToken = response.nextToken;

      if (remaining) {
        remaining -= response.mappings.length;
      }
    } while (nextToken && (!remaining || remaining > 0));

    return mappings;
  }

  private dedupeMappings(mappings: ControlMapping[]): ControlMapping[] {
    const uniqueMappings = new Map<string, ControlMapping>();
    mappings.forEach((mapping) => {
      const key = mapping.mappingKey || `${mapping.controlKey}#${mapping.mappedControlKey}`;
      if (!key) {
        return;
      }
      uniqueMappings.set(key, mapping);
    });
    return Array.from(uniqueMappings.values());
  }

  private isCommonControlsFrameworkKey(frameworkKey?: string): boolean {
    if (!frameworkKey) {
      return false;
    }
    const [frameworkName, frameworkVersion] = frameworkKey.split('#');
    return (
      this.normalizeFrameworkName(frameworkName || '') === 'commoncontrols' &&
      frameworkVersion ===
        NexusApiOrchestrationService.COMMON_CONTROLS_FRAMEWORK_VERSION
    );
  }

  private shuffle<T>(items: T[]): T[] {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  private isThrottlingError(error: unknown): boolean {
    const err = error as {
      statusCode?: number;
      message?: string;
      code?: string;
      name?: string;
    };
    if (err?.statusCode === 429) {
      return true;
    }
    const combined = `${err?.name || ''} ${err?.code || ''} ${err?.message || ''}`;
    return /429|TooManyRequests|throttl/i.test(combined);
  }

  private async delay(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const nexusApiOrchestrationService = new NexusApiOrchestrationService();
